import {Trans} from '@lingui/macro';
import {transparentize} from 'polished';
import React, {ReactNode, useState} from 'react';
import styled from 'styled-components';

import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {AsyncHandlerOf, Handler, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useWasUnmounted} from '../../../../srcCommon/helpers/hookHelpers';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {InlineEditingProps, useInlineEditing} from '../../common/inlineEditing/useInlineEditing';
import {useContactDetailsContext} from '../contactDetailsContext';

/*
 * Props.
 */

interface ContactInformationAttributeProps<T = string> extends InteractionComponentProps {
  title?: ReactNode;
  style?: React.CSSProperties;
  controlsStyle?: React.CSSProperties;
  renderValue: RendererOf<InlineEditingProps<T>>;
  currentAttributeValue: T;
  copyValue?: string;
  onSave: AsyncHandlerOf<T>;
  onDelete: Handler;
  shouldStartInEditMode: boolean;
  shouldHideCopyOption?: boolean;
  customEditLabel?: React.ReactNode;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  margin: 10px 0;
`;

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
`;

const StyledValueDiv = styled.div`
  font-size: ${fontSizes.medium};
  position: relative;
  display: flex;
  align-items: center;
  overflow-wrap: anywhere;

  /* Overrides the p formatting that comes with LinkifyFormatter that some values use */
  p {
    margin: 0;
  }

  a {
    color: ${(p) => p.theme.palette.blue.shade40};
    cursor: pointer;
  }
`;

const StyledControlsDiv = styled.div`
  position: absolute;
  right: 0px;
  top: 4px;
  padding-left: 16px;
  background: linear-gradient(
    to right,
    ${(p) => transparentize(1, p.theme.greys.white)} 0%,
    ${(p) => p.theme.greys.white} 16px
  );
  display: flex;
`;

/*
 * Component.
 */

export const ContactInformationAttribute = <T extends {}>(props: ContactInformationAttributeProps<T>) => {
  const theme = useUserTheme();
  const {
    title,
    style,
    controlsStyle,
    renderValue,
    currentAttributeValue,
    copyValue,
    onSave,
    onDelete,
    shouldStartInEditMode,
    shouldHideCopyOption,
    customEditLabel,
  } = props;

  const {copy} = useClipboard();
  const [wasJustCopied, setWasJustCopied] = useState(false);
  const [shouldShowControls, setShouldShowControls] = useState(false);
  const {wasUnmountedRef} = useWasUnmounted();
  const inlineEditingProps = useInlineEditing<T>({
    currentAttributeValue,
    onSave,
    shouldStartInEditMode,
    wasUnmounted: wasUnmountedRef.current,
  });
  const contactDetailsContext = useContactDetailsContext();

  const showControls = () => {
    // Don't show the hover controls while the user is editing or saving
    if (inlineEditingProps.isEditing || inlineEditingProps.isSaving) {
      return;
    }
    if (contactDetailsContext.isReadOnly) {
      return;
    }
    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  const startEditing = () => {
    hideControls();
    inlineEditingProps.onEditStart();
  };

  const onCopyClick = () => {
    copy(`${copyValue ?? currentAttributeValue}`);
    setWasJustCopied(true);
  };

  return (
    <StyledContentDiv style={style} {...buildInteractionIdProps(props.interactionId)}>
      {title && <StyledTitleDiv>{title}</StyledTitleDiv>}
      <StyledValueDiv
        onMouseEnter={showControls}
        onMouseMove={showControls}
        onMouseLeave={() => {
          hideControls();
          setWasJustCopied(false);
        }}
        {...buildInteractionIdProps('contact-information-attribute-value')}
      >
        {renderValue(inlineEditingProps)}
        {shouldShowControls && (
          <StyledControlsDiv style={controlsStyle}>
            {!shouldHideCopyOption && renderCopyButton(wasJustCopied, onCopyClick)}
            <IconButton
              iconName="editSmall"
              color={theme.greys.shade70}
              label={
                customEditLabel ?? <Trans id="contact-details-contact-information-attribute-edit">Edit</Trans>
              }
              onClick={startEditing}
              interactionId="contact-information-attribute-edit"
            />
            <IconButton
              iconName="trashSmall"
              color={theme.greys.shade70}
              label={<Trans id="contact-details-contact-information-attribute-remove">Remove</Trans>}
              onClick={onDelete}
              interactionId="contact-information-attribute-remove"
            />
          </StyledControlsDiv>
        )}
      </StyledValueDiv>
    </StyledContentDiv>
  );
};

/*
 * Render helpers.
 */

const renderCopyButton = (wasJustCopied: boolean, onCopyClick: Handler) => (
  <>
    {wasJustCopied ? (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName="checkmarkBoxSmall"
            label={<Trans id="contact-details-contact-information-attribute-copied">Copied!</Trans>}
            hoverColor={theme.palette.green.shade40}
          />
        )}
      </UserThemeConsumer>
    ) : (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName="copySmall"
            color={theme.greys.shade70}
            label={<Trans id="contact-details-contact-information-attribute-copy">Copy</Trans>}
            onClick={onCopyClick}
            interactionId="contact-information-attribute-copy"
          />
        )}
      </UserThemeConsumer>
    )}
  </>
);
